// =============================================================================
// supabase/functions/_shared/unleashed/contactMap.ts
// -----------------------------------------------------------------------------
// PURE mappers for the Customer CONTACT sync (Contacts arm). No Deno, no imports —
// shared by the push-contacts Edge Function AND the vitest suite (mirrors
// customerMap.ts / productMap.ts).
//
// A Contact = a user_shelters MEMBERSHIP (the person on ONE service). A person on N
// services is N contacts, each with its OWN GUID (user_shelters.unleashed_contact_guid),
// because Unleashed has no notion of one person spanning several Customers. The
// endpoint is POST/PUT/DELETE /Customers/{customerGuid}/Contacts[/{contactGuid}].
//
// ⚠️ SANDBOX-VERIFY (blind field-name assumptions, isolated here — a wrong guess is a
//    one-field fix once a live GET /Customers/{guid}/Contacts is seen):
//   • Phones: mobile → MobilePhone, landline phone → PhoneNumber. The API also exposes
//     OfficePhone; confirm which the portal's landline should populate.
//   • PUT overwrites omitted fields with blanks (no partial update) → mergeContactForUpdate
//     sends the FULL body and CARRIES the Unleashed-only contact fields (role flags /
//     Notes / other phones) from the GET so they aren't wiped.
//   • The create-response GUID shape is handled in pushContact.ts (transport), not here.
// =============================================================================

// One Unleashed contact reduced to what the Link backfill matches on.
export interface SlimContact {
  guid: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

// The subset of a membership contact the payload needs. Kept loose (import-free) so
// this stays Deno-safe; the caller (pushContact.loadMembership) passes the plain
// fields off user_shelters + the computed primary flag.
export interface ContactPushInput {
  first_name: string | null;
  last_name: string | null;
  // The contact email IS the person's login email (user_shelters.email).
  email: string | null;
  phone: string | null; // landline → PhoneNumber (SANDBOX-VERIFY)
  mobile: string | null; // mobile   → MobilePhone (SANDBOX-VERIFY)
  // IsDefault = this membership is the service's PRIMARY contact
  // (shelters.primary_contact === user_shelters.id). Computed by the caller.
  is_default: boolean;
}

const trimStr = (v: unknown): string => String(v ?? "").trim();
const strOrNull = (v: unknown): string | null => {
  const s = trimStr(v);
  return s === "" ? null : s;
};

// One Unleashed contact row (a GET /Customers/{guid}/Contacts item, or a Contacts[]
// entry on a Customer GET) → the slim shape the Link backfill matches on (email, else
// First+Last).
export function mapUnleashedContact(raw: Record<string, unknown>): SlimContact {
  return {
    guid: typeof raw.Guid === "string" ? raw.Guid : null,
    firstName: strOrNull(raw.FirstName),
    lastName: strOrNull(raw.LastName),
    email: strOrNull(raw.EmailAddress),
  };
}

// Every contact on a raw GET /Customers row → SlimContact[]. The Link backfill reads
// contacts from the ONE Customer GET (Contacts[] returns on it), so there's no
// per-contact call. Non-object entries are ignored.
export function contactsFromCustomer(rawCustomer: Record<string, unknown>): SlimContact[] {
  const arr = Array.isArray(rawCustomer.Contacts)
    ? (rawCustomer.Contacts as Record<string, unknown>[])
    : [];
  return arr.filter((c) => c && typeof c === "object").map(mapUnleashedContact);
}

// One membership contact → the POST/PUT body. `guid` is included ONLY for a PUT
// (update); on a POST (create) it's omitted — Unleashed assigns the GUID and returns
// it. Blank name/email/phone fields are omitted; IsDefault is always sent.
export function buildContactPayload(
  input: ContactPushInput,
  guid?: string | null
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const fn = strOrNull(input.first_name);
  const ln = strOrNull(input.last_name);
  const em = strOrNull(input.email);
  const mob = strOrNull(input.mobile);
  const ph = strOrNull(input.phone);
  if (fn) body.FirstName = fn;
  if (ln) body.LastName = ln;
  if (em) body.EmailAddress = em;
  if (mob) body.MobilePhone = mob;
  if (ph) body.PhoneNumber = ph;
  body.IsDefault = input.is_default === true;
  if (guid) body.Guid = guid;
  return body;
}

// Unleashed-only contact fields the portal doesn't manage but a PUT would BLANK if
// omitted (no partial update). Carried from the current GET so an update never wipes
// them. All are documented as scalars, so carrying is POST-safe (confirm on sandbox).
const CARRY_CONTACT_FIELDS = [
  "OfficePhone",
  "FaxNumber",
  "TollFreeNumber",
  "DDINumber",
  "DeliveryAddress",
  "ForInvoicing",
  "ForOrdering",
  "ForShipping",
  "Notes",
];

// READ-MODIFY-WRITE for a PUT update: build the full managed body (with the required
// Guid from the GET) and overlay the carried Unleashed-only fields from `existing`
// so the update never blanks a field the portal doesn't manage.
export function mergeContactForUpdate(
  existing: Record<string, unknown>,
  input: ContactPushInput
): Record<string, unknown> {
  const guid = typeof existing.Guid === "string" ? existing.Guid : "";
  const body = buildContactPayload(input, guid);
  for (const k of CARRY_CONTACT_FIELDS) {
    const v = existing[k];
    if (v !== undefined && v !== null) body[k] = v;
  }
  return body;
}
